import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chessAPI, platformAPI } from '../services/api';

export default function ChessTournamentLobby() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [name, setName] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [timeControlSeconds, setTimeControlSeconds] = useState(180);
  const [entryWager, setEntryWager] = useState(0);
  const [minimumWager, setMinimumWager] = useState(10);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadPlatformSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMin = Number(res?.data?.minimumWager);
        if (Number.isFinite(nextMin) && nextMin >= 0) {
          setMinimumWager(nextMin);
        }
      } catch (err) {
        console.error('Failed to load platform settings:', err);
      }
    };
    loadPlatformSettings();
  }, []);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const res = await chessAPI.listTournaments();
      setTournaments(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTournaments();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Arena name is required.');
      return;
    }
    const safeDuration = Math.min(120, Math.max(10, durationMinutes));

    const wager = Number(entryWager) || 0;
    if (wager > 0 && wager < minimumWager) {
      setError(`Wager must be at least ${minimumWager} wikipenny, or 0 for friendly.`);
      return;
    }

    if (wager > 0 && Number(user?.balance || 0) < wager) {
      setError(`Insufficient balance. You need ${wager} wikipenny.`);
      return;
    }

    try {
      setLoading(true);
      const res = await chessAPI.createTournament({ 
        name, 
        durationMinutes: safeDuration, 
        timeControlSeconds,
        entryWager: wager
      });
      navigate(`/chess/tournaments/${res.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (tournamentId, entryWagerAmount) => {
    const wager = Number(entryWagerAmount || 0);
    if (wager > 0 && Number(user?.balance || 0) < wager) {
      setError(`Insufficient balance. You need ${wager} wikipenny to join this arena.`);
      return;
    }

    try {
      setLoading(true);
      const res = await chessAPI.joinTournament(tournamentId);
      navigate(`/chess/tournaments/${res.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to join tournament');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tournamentId) => {
    if (!window.confirm('Are you sure you want to delete this Arena? All player registrations will be removed.')) return;
    try {
      setLoading(true);
      await chessAPI.cancelTournament(tournamentId);
      loadTournaments();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete tournament');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="dashboard-content">
        <div className="chess-lobby-header">
          <div className="header-left">
            <div className="header-icon">🔥</div>
            <div>
              <h1 className="page-title">Chess Arena</h1>
              <p className="page-subtitle">Play continuously, crush your opponents, and collect points!</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate('/chess')}>
            ← Back to Chess
          </button>
        </div>

        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        <div className="quick-actions-card">
          <div className="section-header">
            <h2 className="section-title">Create Arena</h2>
            <p className="section-subtitle">Set up a continuous-play Arena where the highest score wins.</p>
          </div>

          <div className="form-grid">
            <label>
              Arena name
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Midnight Blitz Arena" required />
            </label>
            <label>
              Arena Duration (10-120 min)
              <input type="number" min="10" max="120" value={durationMinutes} onChange={(e) => setDurationMinutes(Number(e.target.value) || 10)} />
            </label>
            <label>
              Time control
              <select 
                value={timeControlSeconds} 
                onChange={(e) => setTimeControlSeconds(Number(e.target.value))}
                style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '8px' }}
              >
                <option value={60} style={{ background: '#1e293b' }}>1 min (Bullet)</option>
                <option value={180} style={{ background: '#1e293b' }}>3 min (Blitz)</option>
                <option value={300} style={{ background: '#1e293b' }}>5 min (Blitz)</option>
              </select>
            </label>
            <label>
              Entry Wager (0 for friendly)
              <input type="number" min="0" value={entryWager} onChange={(e) => setEntryWager(e.target.value === '' ? '' : Number(e.target.value))} />
            </label>
            <div style={{ gridColumn: '1 / -1', marginTop: 8, padding: '12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, fontSize: 13, color: 'rgba(255,255,255,0.5)', border: '1px dashed rgba(255,255,255,0.1)' }}>
              ℹ️ Maximum 32 players allowed per arena. In-progress joining is supported.
            </div>
          </div>

          <button className="flappy-btn-primary" onClick={handleCreate} disabled={loading} style={{ background: '#e11d48', borderColor: '#be123c' }}>
            Launch Arena
          </button>
        </div>

        <div className="matches-section">
          {/* Active / Pending Arenas */}
          <div className="section-header">
            <h2 className="section-title">Open Arenas</h2>
            <p className="section-subtitle">Join an active arena and immediately start playing.</p>
          </div>

          {loading && <div>Loading arenas…</div>}
          {!loading && tournaments.filter(t => t.status !== 'completed').length === 0 && (
            <div style={{ padding: '24px', background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px dashed rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginBottom: 40 }}>
              No open arenas at the moment. Create one above!
            </div>
          )}

          <div className="matches-list">
            {tournaments.filter(t => t.status !== 'completed').map((tournament) => (
              <div key={tournament.id} className="match-card chess-tournament-card">
                <div className="match-card-main">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <div className="match-title">{tournament.name}</div>
                      <span className={`challenge-badge ${Number(tournament.entry_wager || 0) > 0 ? 'real' : 'friendly'}`} style={{ 
                        fontSize: 10, 
                        padding: '2px 8px', 
                        borderRadius: 4, 
                        textTransform: 'uppercase',
                        background: Number(tournament.entry_wager || 0) > 0 ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #6366f1, #4338ca)',
                        color: 'white',
                        fontWeight: 'bold',
                        letterSpacing: '0.5px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        {Number(tournament.entry_wager || 0) > 0 ? '💰 REAL' : '🎮 FRIENDLY'}
                      </span>
                      <span style={{ 
                        fontSize: 10, 
                        padding: '2px 8px', 
                        borderRadius: 4, 
                        textTransform: 'uppercase',
                        background: tournament.status === 'active' ? '#10b981' : '#f59e0b',
                        color: 'white'
                      }}>
                        {tournament.status}
                      </span>
                    </div>
                    <div className="match-meta">
                      {tournament.player_count || 0} players • {tournament.duration_minutes || 30} mins
                      {Number(tournament.entry_wager || 0) > 0 && ` • Wager: ${tournament.entry_wager} wkp`}
                    </div>
                  </div>
                  <button className="action-btn play" onClick={() => navigate(`/chess/tournaments/${tournament.id}`)}>
                    View Arena
                  </button>
                  {tournament.status === 'pending' && Number(tournament.creator_id) === Number(user?.id) && (
                    <button 
                      className="action-btn" 
                      onClick={() => handleDelete(tournament.id)}
                      style={{ background: '#7f1d1d', color: 'white', marginLeft: 8 }}
                    >
                      🗑 Delete
                    </button>
                  )}
                </div>
                <div className="match-card-footer">
                  <div>Created by {tournament.creator_username || 'Unknown'}</div>
                  <button className="action-btn watch" onClick={() => handleJoin(tournament.id, tournament.entry_wager)}>
                    Join
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Past Arenas Section */}
          {tournaments.some(t => t.status === 'completed') && (
            <div style={{ marginTop: 64 }}>
              <div className="section-header">
                <h2 className="section-title">Past Arenas</h2>
                <p className="section-subtitle">Historical records of finished tournaments and winners.</p>
              </div>
              <div className="matches-list" style={{ opacity: 0.7 }}>
                {tournaments.filter(t => t.status === 'completed').map((tournament) => (
                  <div key={tournament.id} className="match-card chess-tournament-card" style={{ filter: 'grayscale(0.5)' }}>
                    <div className="match-card-main">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                          <div className="match-title">{tournament.name}</div>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', background: '#475569', color: 'white' }}>
                            FINISHED
                          </span>
                        </div>
                        <div className="match-meta">
                           Ended • Winner: {tournament.winner_username || 'None'}
                        </div>
                      </div>
                      <button className="action-btn watch" onClick={() => navigate(`/chess/tournaments/${tournament.id}`)}>
                        Results
                      </button>
                    </div>
                    <div className="match-card-footer">
                      <div>Final Player Count: {tournament.player_count || 0}</div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{new Date(tournament.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
